import { axiosMultipart } from "@/axios/interceptors";
import { instanceOfError, parseRequestError } from "@/axios/utils.ts";
import { ZodSentryReporter } from "@/lib/zod-sentry-reporter";
import { z } from "zod";

export const AuthTokenResponseSchema = z.object({
	access_token: z.string(),
	token_type: z.literal("Bearer"),
	expires_in: z.number(),
	purpose: z.string().nullable().optional(),
});
export type AuthTokenResponse = z.infer<typeof AuthTokenResponseSchema>;

export const FaceAuthErrorCode = {
	multiple_matches: 10904,
	new_user_or_unverified_user_detected: 10690,
	eyes_closed: 10831,
	face_mask_detected: 10833,
	glasses_detected: 10887,
	no_face_detected: 10685,
	obstruction_detected: 10890,
	face_not_centered: 10828,
	face_too_close: 10829,
	low_quality: 10827,
	face_too_far: 10830,
	image_too_dark: 10834,
	image_too_light: 10835,
	image_too_blurry: 11020,
	face_has_extreme_yaw: 11028,
	face_has_extreme_pitch: 11027,
} as const;

export const FaceAuthCorrectableErrorCodes: readonly number[] = [
	FaceAuthErrorCode.eyes_closed,
	FaceAuthErrorCode.face_mask_detected,
	FaceAuthErrorCode.glasses_detected,
	FaceAuthErrorCode.no_face_detected,
	FaceAuthErrorCode.obstruction_detected,
	FaceAuthErrorCode.face_not_centered,
	FaceAuthErrorCode.face_too_close,
	FaceAuthErrorCode.low_quality,
	FaceAuthErrorCode.face_too_far,
	FaceAuthErrorCode.image_too_dark,
	FaceAuthErrorCode.image_too_light,
	FaceAuthErrorCode.image_too_blurry,
	FaceAuthErrorCode.face_has_extreme_yaw,
	FaceAuthErrorCode.face_has_extreme_pitch,
];

export const CandidateSchema = z.object({
	similarity_measure: z.number(),
	match_probability: z.number(),
	identity: z.number(),
	UUID: z.string(),
});
export const MultipleMatchesDetailsSchema = z.object({
	candidates: z.array(CandidateSchema),
	log_id: z.number(),
});

export type MultipleMatchesDetails = z.infer<
	typeof MultipleMatchesDetailsSchema
>;

export type UploadSelfieParams = {
	imageBlob: Blob;
};

export async function uploadSelfie({
	imageBlob,
}: UploadSelfieParams): Promise<AuthTokenResponse> {
	const form = new FormData();
	form.append("file", imageBlob, "selfie.jpg");
	form.append("image_type", "selfie");

	try {
		// axiosMultipart has interceptors to:
		//  - strip Authorization when { public: true }
		//  - remove Content-Type so browser sets boundary
		//  - ensure FormData and append default params (user_id, location_id, global_on_id)
		const response = await axiosMultipart.post(
			"/biometrics/face_id/auth",
			form,
			{
				public: true,
			},
		);
		return ZodSentryReporter.parse(AuthTokenResponseSchema, response);
	} catch (err) {
		if (instanceOfError(err)) {
			throw err;
		}
		throw parseRequestError(err);
	}
}
